import { Controller } from "@hotwired/stimulus"

// Mobile (<768px) off-canvas drawer for the app-shell sidebar.
//
// Toggles `<html data-sidebar-drawer-open="true|false">`, which the
// drawer + scrim CSS in app.tailwind.css reads to slide the existing
// <aside id="app-drawer"> in/out. Reuses the SAME sidebar markup — no
// duplicate mobile nav. Desktop (>=768px) ignores the attribute (the
// drawer CSS is gated behind a max-width media query), so this is inert
// on laptops/desktops.
//
// Attached to <body> so its scope spans the hamburger (in the top bar),
// the <aside>, and the scrim — all siblings under <body>. Registered in
// controllers/index.js as "sidebar-drawer"; rebuild with bin/webpack
// after adding (new controllers are dead until registered + recompiled).
export default class extends Controller {
  static targets = ["toggle"]

  open() {
    this.setOpen(true)
  }

  close() {
    this.setOpen(false)
  }

  toggle() {
    this.setOpen(document.documentElement.dataset.sidebarDrawerOpen !== "true")
  }

  // Reset on connect so a Turbo navigation never leaves the drawer open
  // (Turbo replaces <body> and re-runs connect on each visit).
  connect() {
    this.setOpen(false)
  }

  disconnect() {
    document.body.classList.remove("drawer-open")
    // Clear any pinned-body styles without restoring scroll — the body is
    // being torn down (Turbo navigation), so a window.scrollTo here would
    // fight Turbo's own scroll restoration on the incoming page.
    this.unlockScroll(false)
  }

  setOpen(open) {
    const root = document.documentElement
    const wasOpen = root.dataset.sidebarDrawerOpen === "true"
    root.dataset.sidebarDrawerOpen = open ? "true" : "false"
    document.body.classList.toggle("drawer-open", open)
    // Lock/unlock background scroll. Reconciled on every call (not just real
    // state changes) so a restored Turbo cache snapshot that baked in the
    // pinned-body inline styles always gets cleared on connect()'s setOpen(false).
    if (open) {
      this.lockScroll()
    } else {
      this.unlockScroll(wasOpen)
    }
    if (this.hasToggleTarget) {
      this.toggleTarget.setAttribute("aria-expanded", open ? "true" : "false")
      this.toggleTarget.setAttribute(
        "aria-label",
        open ? "Close navigation menu" : "Open navigation menu"
      )
    }
    // The shared <aside id="app-drawer"> is a modal surface only at
    // <768px, where it slides over the page behind a scrim. While open,
    // promote it to a named modal dialog so assistive tech announces it as
    // a dialog and treats the content behind the scrim as inert (aria-modal
    // is only honored alongside a dialog role, and a dialog needs an
    // accessible name). On close, strip the three attributes to restore the
    // aside's default complementary landmark. Inert on desktop: setOpen(true)
    // only fires from the md:hidden hamburger, and connect()'s setOpen(false)
    // clears the attributes on every page load.
    const drawer = document.getElementById("app-drawer")
    if (drawer) {
      if (open) {
        drawer.setAttribute("role", "dialog")
        drawer.setAttribute("aria-modal", "true")
        drawer.setAttribute("aria-label", "Navigation menu")
      } else {
        drawer.removeAttribute("role")
        drawer.removeAttribute("aria-modal")
        drawer.removeAttribute("aria-label")
      }
    }
    // Focus management, guarded on a real state change so connect()'s
    // initial setOpen(false) and desktop pages never steal focus: move
    // focus into the drawer on open, back to the hamburger on close.
    if (open && !wasOpen) {
      const first = drawer && drawer.querySelector("a, button")
      if (first) first.focus()
    } else if (!open && wasOpen && this.hasToggleTarget) {
      this.toggleTarget.focus()
    }
  }

  // iOS Safari ignores `overflow: hidden` on <body> for touch/inertial
  // scrolling (a long-standing WebKit quirk), letting the page scroll
  // through the scrim. Pin the body instead: capture the scroll offset and
  // shift the body up by it with position: fixed so the page is frozen in
  // place. The `scrollLocked` guard stops a double-open from re-capturing
  // scrollY as 0 (a pinned body always reports window.scrollY === 0).
  lockScroll() {
    if (this.scrollLocked) return
    this.savedScrollY = window.scrollY
    this.scrollLocked = true
    const style = document.body.style
    style.position = "fixed"
    style.top = `-${this.savedScrollY}px`
    style.width = "100%"
  }

  // Remove the pinned-body styles and, when `restore` is set and we actually
  // locked, jump back to the saved offset (position: fixed reset the page to
  // the top). Always clears the inline styles so a stale snapshot can't leave
  // the body stuck.
  unlockScroll(restore) {
    const style = document.body.style
    style.position = ""
    style.top = ""
    style.width = ""
    if (restore && this.scrollLocked && typeof this.savedScrollY === "number") {
      window.scrollTo(0, this.savedScrollY)
    }
    this.scrollLocked = false
    this.savedScrollY = null
  }
}
